import { lighten, tint } from 'polished';
import styled from 'styled-components';

import { NOTIFICATION_TYPES } from '@savgroup-front-common/constants';
import {
  DefaultStyledProps,
  SUPPORTED_COMPONENTS,
  SUPPORTED_PROPERTIES,
} from '@savgroup-front-common/types';

import { rem } from '../../helpers';
import { styledGetCustomComponentProperty } from '../../helpers/theme';

interface NotificationTheming extends DefaultStyledProps {
  hollow?: boolean;
  notificationType?: NOTIFICATION_TYPES;
  $isNewUiEnabled?: boolean;
}

export const iconColorBasedOnProps = ({
  theme,
  notificationType,
}: NotificationTheming): string => {
  if (notificationType === NOTIFICATION_TYPES.INFO) {
    return theme.newUI.tagsColors.primary.color;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS) {
    return theme.newUI.tagsColors.success.color;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT) {
    return theme.newUI.tagsColors.alert.color;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR) {
    return theme.newUI.tagsColors.danger.color;
  }

  return theme.newUI.iconColors.primary.color;
};

export const colorBasedOnProps = ({
  theme,
  notificationType,
  $isNewUiEnabled,
}: NotificationTheming): string => {
  if (notificationType === NOTIFICATION_TYPES.INFO && $isNewUiEnabled) {
    return theme.newUI.tagsColors.primary.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.INFO) {
    return theme.colors.primary;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS && $isNewUiEnabled) {
    return theme.newUI.tagsColors.success.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.SUCCESS) {
    return theme.colors.success;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT && $isNewUiEnabled) {
    return theme.newUI.tagsColors.alert.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.ALERT) {
    return theme.colors.alert;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR && $isNewUiEnabled) {
    return theme.newUI.tagsColors.danger.bgColor;
  }
  if (notificationType === NOTIFICATION_TYPES.ERROR) {
    return theme.colors.danger;
  }
  if (notificationType === NOTIFICATION_TYPES.DEFAULT) {
    return theme.colors.white;
  }

  return theme.colors.primary;
};

export const colorBasedOnPropsIncludingHollow = ({
  theme,
  notificationType,
  hollow,
}: NotificationTheming): string => {
  if (hollow) {
    return colorBasedOnProps({
      theme,
      notificationType,
      hollow,
    });
  }

  return theme.colors.white;
};

interface NotificationWrapperProps extends NotificationTheming {
  $isFluid: boolean;
  notificationType?: NOTIFICATION_TYPES;
  $isNewUiEnabled?: boolean;
  $withMarginBottom?: boolean;
  $isBold?: boolean;
}

export const $NotificationWrapper = styled.div<NotificationWrapperProps>`
  height: auto;
  overflow: hidden;

  font-size: ${({ theme }) => theme.fonts.size.small};

  color: ${({ $isNewUiEnabled, theme }) =>
    $isNewUiEnabled
      ? theme.systemColors.defaultTextColor
      : colorBasedOnPropsIncludingHollow};
  svg {
    color: ${({ $isNewUiEnabled, theme, notificationType }) =>
      $isNewUiEnabled
        ? iconColorBasedOnProps({ theme, notificationType })
        : colorBasedOnPropsIncludingHollow};
  }

  font-weight: ${({ theme, $isBold }) =>
    $isBold ? theme.fonts.weight.bold : theme.fonts.weight.mediumFont};

  background-color: ${(props) => {
    const color = colorBasedOnProps(props);

    if (props.hollow) {
      if (props.$isNewUiEnabled) {
        return props.theme.newUI.tagsColors.alert.bgColor;
      }

      return tint(0.94, color);
    }

    return color;
  }};

  border-radius: ${({ theme, $isNewUiEnabled }) =>
    $isNewUiEnabled ? theme.newUI.borders.bigRadius : theme.borders.radius};
  border: ${({ $isNewUiEnabled, theme, hollow, notificationType }) =>
    ($isNewUiEnabled &&
      !hollow &&
      notificationType === NOTIFICATION_TYPES.INFO) ||
    notificationType === NOTIFICATION_TYPES.DEFAULT
      ? `1px solid ${theme.newUI.variationColors?.light4}`
      : null};

  max-width: ${({ $isFluid }) => ($isFluid ? null : rem(550))};

  margin: 0 auto;
  margin-bottom: ${({ $withMarginBottom }) =>
    $withMarginBottom ? '1rem' : null};

  display: flex;
  align-items: center;
  min-height: ${styledGetCustomComponentProperty({
    componentName: SUPPORTED_COMPONENTS.DEFAULT_INPUTS,
    property: SUPPORTED_PROPERTIES.HEIGHT,
  })};
`;

export const $IconContainer = styled.div<{ $isNewUiEnabled: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  min-width: ${rem(50)};
`;

interface TextContainerProps {
  $hasIcon: boolean;
}

const textPadding = 7;

export const $TextContainer = styled.div<TextContainerProps>`
  flex: 1;
  padding: ${({ $hasIcon }) =>
    $hasIcon ? `${rem(textPadding)} 0 ${rem(textPadding)}` : rem(textPadding)};
  &:last-child {
    padding-right: 1.1rem;
  }
`;

interface NotificationContainerProps {
  $height: number;
}

export const $NotificationContainer = styled.div<NotificationContainerProps>`
  &:last-child {
    padding-bottom: 0;
  }
`;

type CloseContainerProps = NotificationTheming;

export const $CloseContainer = styled.button<CloseContainerProps>`
  min-width: ${rem(24)};
  background: none;
  outline: none;
  border: 0;
  cursor: pointer;
  margin-right: ${rem(10)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  transition-duration: 200ms;

  svg {
    color: ${({ theme, notificationType }) =>
      iconColorBasedOnProps({ theme, notificationType })};
  }

  &:hover,
  &:focus {
    background-color: ${(props) =>
      props.hollow
        ? colorBasedOnProps(props)
        : lighten(0.15, colorBasedOnProps(props))};
  }
`;
